'use client';

/**
 * Global watcher that keeps auto-verify pipelines observable even after the
 * user has navigated away from the originating session.
 *
 * Mounted once at the app root. It subscribes to `inFlightAutoVerifySessionsAtom`
 * and, for every in-flight session, renders a child component that keeps the
 * analysis query polling. When a session transitions to a terminal status and
 * the user is not currently viewing it, we fire the existing cross-session
 * toast (same shape as the manual analyze flow).
 */

import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import * as React from 'react';
import { useIntl } from 'react-intl';

import { toast, ToastActionButton } from '@heidi/design-system/toast';
import { logger } from '@heidi/runtime/logger';
import { type NavigatePort } from '@heidi/runtime/shell';

import {
  autoVerifyGenerationLifecycleAtom,
  autoVerifyPopoverSessionAtom,
  inFlightAutoVerifySessionsAtom,
  isAnalyzingBySessionAtom,
  resolveAutoVerifyGenerationAtom,
} from '../atoms';
import {
  getActiveSessionIdFromUrl,
  isIntegrationRoute,
} from '../utils/getActiveSessionIdFromUrl';

import { autoVerifyCrossSessionWatcherMessages as messages } from './AutoVerifyCrossSessionWatcher.messages';
import { SessionPoller } from './SessionPoller';

export function AutoVerifyCrossSessionWatcher({
  onNavigateToSession,
}: {
  onNavigateToSession: NavigatePort;
}) {
  const intl = useIntl();
  const [inFlight, setInFlight] = useAtom(inFlightAutoVerifySessionsAtom);
  const generationLifecycles = useAtomValue(autoVerifyGenerationLifecycleAtom);
  const setIsAnalyzing = useSetAtom(isAnalyzingBySessionAtom);
  const setPopoverSession = useSetAtom(autoVerifyPopoverSessionAtom);
  const resolveGeneration = useSetAtom(resolveAutoVerifyGenerationAtom);

  const handleTerminal = React.useCallback(
    (sessionId: string, status: 'completed' | 'failed', analysisId: string) => {
      resolveGeneration({ sessionId, analysisId });
      setInFlight((prev) => {
        if (!prev.has(sessionId)) return prev;
        const next = new Set(prev);
        next.delete(sessionId);
        return next;
      });
      setIsAnalyzing((prev) => {
        if (!prev[sessionId]) return prev;
        return { ...prev, [sessionId]: false };
      });

      if (status === 'failed') {
        logger.warn('Auto-verify cross-session watcher: pipeline failed', {
          sessionId,
          analysisId,
        });
        return;
      }

      // Never toast inside an embedded EHR integration context — the action
      // deep-links to /scribe/session/..., which would navigate the clinician
      // out of the embed into the standalone web app.
      if (isIntegrationRoute()) return;

      // Only surface a cross-session toast when the user has navigated away.
      // In-session completion is handled by `useAutoVerify`, which shows the
      // anchored popover instead.
      const activeSessionId = getActiveSessionIdFromUrl();
      if (activeSessionId === sessionId) return;

      toast({
        tone: 'success',
        title: intl.formatMessage(messages.readyTitle),
        description: intl.formatMessage(messages.readyOtherSessionDescription),
        action: (
          <ToastActionButton
            tone="success"
            onClick={() => {
              // Clear any stale popover state so deep-linking to the session
              // lands on the drawer only (no popover duplication).
              setPopoverSession(null);
              void onNavigateToSession(`/scribe/session/${sessionId}?openEV=true`);
            }}
          >
            {intl.formatMessage(messages.viewResults)}
          </ToastActionButton>
        ),
      });
    },
    [
      resolveGeneration,
      setInFlight,
      setIsAnalyzing,
      setPopoverSession,
      intl,
      onNavigateToSession,
    ]
  );

  const sessionIds = React.useMemo(() => Array.from(inFlight), [inFlight]);

  return (
    <>
      {sessionIds.map((id) => (
        <SessionPoller
          key={id}
          sessionId={id}
          expectedAnalysisId={generationLifecycles[id]?.analysisId}
          onTerminal={handleTerminal}
        />
      ))}
    </>
  );
}
